/**
 * World state — where a student stands in the RPG world, derived.
 *
 * Nothing here is stored. A kingdom's status is a pure function of the kingdom
 * definitions, the concept graph, the student's per-concept state and the list
 * of bosses they have beaten, in the same way `assignmentProgress` is a pure
 * function of an assignment and a profile: there is no seal flag to go stale,
 * and a world that re-derives itself cannot disagree with the mastery it is
 * drawn from.
 *
 * Every time-dependent function takes `now`. Retention decays, so "known" is a
 * question about a moment, and a `Date.now()` in here would make the whole
 * world untestable.
 *
 * `worldState` answers "is this kingdom open", never "may this student open
 * this lesson" — see `types.ts`. The adventure does refuse a sealed island, and
 * the mathematics inside it stays reachable from the ordinary routes; nothing
 * here is ever the answer to a question about a lesson.
 */

import type { ConceptGraph } from '@yuan-math/curriculum';
import { knownSet, masteredSet, type ConceptState } from '@yuan-math/learning';
import type { Kingdom, KingdomStatus, Region } from './types.ts';

/** A gap between where the student is and what a kingdom asks for. */
export type MissingRequirement =
  /**
   * `conceptIds` are the concepts of the clause the student does not know yet —
   * the gap itself, so a page can name it — while `have` and `need` count
   * against the whole clause, which is what "8 of 10" on a bar means.
   */
  | { kind: 'known'; conceptIds: string[]; have: number; need: number }
  | { kind: 'boss'; bossId: string };

/** How much of a region the student has behind them. */
export type RegionProgress = {
  /**
   * Concepts in `knownSet` — mastered, or credited by the placement test and
   * not since contradicted. See `types.ts` for why this is not `masteredSet`.
   */
  known: number;
  /** Every concept the region names, planned ones included. */
  total: number;
  /** The stricter count: mastery earned by practice. */
  mastered: number;
};

export type KingdomState = {
  kingdomId: string;
  status: KingdomStatus;
  /**
   * How much of the unlock requirement is satisfied, 0–1. 1 means open.
   *
   * A kingdom opened because something above it is open reads 1 as well: see
   * `openDownwards`, where the requirement is met by a road that went round it.
   */
  readiness: number;
  /**
   * What the unlock requirement is still waiting for; empty once it is met.
   *
   * Empty too for a kingdom opened by one above it (`openDownwards`): there is
   * nothing left to wait for, so there is nothing to state.
   *
   * A `cleared` kingdom can still list gaps. The boss is down for good, but
   * readiness is read fresh every time and a review coming due can pull it back
   * under the line — which is worth showing a student, and is not a reason to
   * take the story back off them.
   */
  missing: MissingRequirement[];
  /** Per-region rollups, in the kingdom's own region order. */
  regions: { regionId: string; progress: RegionProgress }[];
};

/**
 * The status of every kingdom, in `order`.
 *
 * `clearedBossIds` comes from the student's profile in PR 2; until then callers
 * pass an empty list and every kingdom with a boss stays `open` at most.
 */
export function worldState(
  kingdoms: readonly Kingdom[],
  graph: ConceptGraph,
  conceptStates: Readonly<Record<string, ConceptState>>,
  clearedBossIds: readonly string[],
  now: number,
): KingdomState[] {
  /*
   * `knownSet`, not `masteredSet`, and the difference decides what a new
   * student's map looks like. Mastery is a claim the platform makes about a
   * student and is only ever earned by practice (CLAUDE.md §1, §10), so a
   * learner who has just placed into Grade 11 has mastered nothing at all.
   * `knownSet` adds what the placement test credited and is the set the
   * platform asks "can they do this yet?" with — the same set the frontier and
   * the Math Map's readiness are drawn from. Sealing this world off mastery
   * would greet that student with eight locked kingdoms and a tutorial village.
   *
   * `masteredSet` is still computed, because a region rollup reports both: the
   * honest claim and the working assumption, side by side.
   */
  const known = knownSet(conceptStates, now);
  const mastered = masteredSet(conceptStates, now);
  const cleared = new Set(clearedBossIds);

  const ordered = [...kingdoms].sort((a, b) => a.order - b.order);
  return openDownwards(
    ordered,
    ordered.map((kingdom) => kingdomState(kingdom, graph, known, mastered, cleared)),
  );
}

/**
 * A kingdom underneath an open one is open — every kingdom underneath it.
 *
 * Each kingdom's requirement is evaluated on its own, and on its own it can say
 * something the rest of the map contradicts: a student whose placement reached
 * the Function Kingdom has that kingdom open while the Fraction Kingdom two
 * roads back reads sealed, because the particular concepts *its* clause names
 * happened not to be among the ones credited. Nobody who has been let into the
 * hard kingdom believes they are not ready for the easy one, and a map that
 * says so reads as a bug rather than as a plan. Unlock the Function Kingdom and
 * you unlock the basics the functions are made of.
 *
 * So openness flows downwards by `order`, and not along the unlock clauses.
 * Clause-following was the first version of this and it is strictly weaker: a
 * requirement may only name kingdoms of lower `order` (`validateWorld`), so
 * every kingdom an edge could reach is one this pass reaches anyway — but a
 * kingdom *no* clause happens to mention stayed sealed underneath an open one,
 * which is the hole the paragraph above is about.
 *
 * Ordering by `order` also buys the second half of the rule the gate needs.
 * Because an open kingdom opens every kingdom below it, `open` is downward
 * closed and `sealed` is therefore *upward* closed: if a kingdom is sealed,
 * every kingdom more advanced than it is sealed too — otherwise this pass would
 * have opened it. That is what lets `apps/web` refuse a sealed island without
 * separately reasoning about what lies beyond it. A single descending scan is
 * enough, since the list arrives sorted.
 *
 * A kingdom opened this way reports `readiness: 1` and no `missing`, because
 * both are answers about a seal that is no longer holding anything back — a
 * page that drew an open kingdom with a 36% bar captioned "of the unlock
 * requirement" would be arguing with the status word beside it. Nothing here
 * ever *closes* a kingdom, and nothing here clears one: a seal that has been
 * satisfied by another road is still not a boss that has been beaten.
 */
function openDownwards(kingdoms: readonly Kingdom[], states: KingdomState[]): KingdomState[] {
  let openAbove = false;
  const out = [...states];
  for (let index = kingdoms.length - 1; index >= 0; index -= 1) {
    const state = out[index] as KingdomState;
    if (state.status !== 'sealed') {
      openAbove = true;
      continue;
    }
    if (openAbove) out[index] = { ...state, status: 'open', readiness: 1, missing: [] };
  }
  return out;
}

function kingdomState(
  kingdom: Kingdom,
  graph: ConceptGraph,
  known: ReadonlySet<string>,
  mastered: ReadonlySet<string>,
  cleared: ReadonlySet<string>,
): KingdomState {
  const clauses = kingdom.unlock.known.map((clause) => evaluateKnownClause(clause, known));
  const bosses = kingdom.unlock.clearedBossIds.map((bossId) => ({
    bossId,
    satisfied: cleared.has(bossId),
  }));

  const scores = [...clauses.map((c) => c.score), ...bosses.map((b) => (b.satisfied ? 1 : 0))];
  // An empty requirement is satisfied, not undefined: Starter Village is open
  // to a student who has done nothing at all, which is the point of it.
  const readiness = scores.length === 0 ? 1 : scores.reduce((sum, score) => sum + score, 0) / scores.length;

  const missing: MissingRequirement[] = [
    ...clauses
      .filter((clause) => !clause.satisfied)
      .map(
        (clause): MissingRequirement => ({
          kind: 'known',
          conceptIds: clause.unknownConceptIds,
          have: clause.have,
          need: clause.need,
        }),
      ),
    ...bosses.filter((boss) => !boss.satisfied).map((boss): MissingRequirement => ({ kind: 'boss', bossId: boss.bossId })),
  ];

  const open = missing.length === 0;

  return {
    kingdomId: kingdom.id,
    status: statusOf(kingdom, graph, known, cleared, open),
    readiness,
    missing,
    regions: kingdom.regions.map((region) => ({
      regionId: region.id,
      progress: rollUp(region, known, mastered),
    })),
  };
}

function statusOf(
  kingdom: Kingdom,
  graph: ConceptGraph,
  known: ReadonlySet<string>,
  cleared: ReadonlySet<string>,
  open: boolean,
): KingdomStatus {
  /*
   * A beaten boss is beaten. The seal is a story state, and the story of a
   * kingdom whose dragon is dead does not reopen because a review came due and
   * readiness slipped back under the line.
   */
  if (kingdom.bossId) {
    if (cleared.has(kingdom.bossId)) return 'cleared';
    return open ? 'open' : 'sealed';
  }

  /*
   * A kingdom with no boss — Starter Village, which has no dragon in it — is
   * finished when there is nothing left in it to learn. Only concepts that can
   * actually be studied count: a region may name planned concepts to show where
   * the mathematics leads, and a kingdom no student could ever finish because it
   * points at content that does not exist yet would be a permanent open task.
   */
  const teachable = kingdom.regions
    .flatMap((region) => region.conceptIds)
    .filter((id) => graph.concepts.get(id)?.status === 'available');
  if (teachable.length > 0 && teachable.every((id) => known.has(id))) return 'cleared';
  return open ? 'open' : 'sealed';
}

function evaluateKnownClause(
  clause: { conceptIds: string[]; share: number },
  known: ReadonlySet<string>,
): { satisfied: boolean; score: number; have: number; need: number; unknownConceptIds: string[] } {
  const total = clause.conceptIds.length;
  // Rounded up: four fifths of three concepts is all three, not two and a bit.
  const need = Math.ceil(clause.share * total);
  const unknownConceptIds = clause.conceptIds.filter((id) => !known.has(id));
  const have = total - unknownConceptIds.length;
  const satisfied = have >= need;
  return {
    satisfied,
    score: need <= 0 ? 1 : Math.min(1, have / need),
    have,
    need,
    unknownConceptIds,
  };
}

/**
 * How far through a region a student is.
 *
 * `total` counts every concept the region names, planned ones included. A
 * region that points beyond what is teachable today therefore reads short of
 * full, which is the honest answer: there is more here than can be studied yet.
 */
export function regionProgress(
  region: Region,
  conceptStates: Readonly<Record<string, ConceptState>>,
  now: number,
): RegionProgress {
  return rollUp(region, knownSet(conceptStates, now), masteredSet(conceptStates, now));
}

/** The same rollup against sets a caller has already built. */
function rollUp(region: Region, known: ReadonlySet<string>, mastered: ReadonlySet<string>): RegionProgress {
  return {
    known: region.conceptIds.filter((id) => known.has(id)).length,
    total: region.conceptIds.length,
    mastered: region.conceptIds.filter((id) => mastered.has(id)).length,
  };
}

/** Every concept a kingdom holds, in region order. A convenience for callers. */
export function kingdomConceptIds(kingdom: Kingdom): string[] {
  return kingdom.regions.flatMap((region) => region.conceptIds);
}

/**
 * Concept id → the kingdom holding it.
 *
 * Every `available` concept belongs to exactly one region of exactly one
 * kingdom and `validateWorld` enforces it, so this is a lookup rather than a
 * choice — the first listing wins only to keep a partial or mid-edit world
 * from throwing at a caller. Built once per call and shared, because a second
 * copy of it in another file is a second answer to "where does this live".
 */
export function conceptKingdoms(kingdoms: readonly Kingdom[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const kingdom of kingdoms) {
    for (const region of kingdom.regions) {
      for (const conceptId of region.conceptIds) {
        if (!out.has(conceptId)) out.set(conceptId, kingdom.id);
      }
    }
  }
  return out;
}
